import { useRef } from "react"
import { insertText } from "@nova/schemas"
import * as Icons from "@nova/component-icons"
import { IconButton } from "@nova/component-btn"
import classNames from "classnames"
import { EmojiPickerDropdown } from "./EmojiPickerDropdown"
import { CommentEditor, CommentEditorHandle } from "./CommentEditor"
import { CommentImageComposeStrip } from "./CommentImageComposeStrip"
import { useCommentImageUpload } from "./useCommentImageUpload"

export type CommentInputProps = {
    value: string
    onChange: (value: string) => void
    onSubmit: () => void
    placeholder?: string
    disabled?: boolean
    mentionDirection?: "up" | "down"
    imageUpload?: ReturnType<typeof useCommentImageUpload>
}

export const CommentInput = ({
    value,
    onChange,
    onSubmit,
    placeholder = "Leave a comment...",
    disabled,
    mentionDirection = "down",
    imageUpload,
}: CommentInputProps) => {
    const editorRef = useRef<CommentEditorHandle>(null)
    const fileInputRef = useRef<HTMLInputElement>(null)
    const insertEmoji = (emoji: string) => {
        if (editorRef.current) editorRef.current.insertText(emoji)
        else onChange(insertText(value, emoji))
    }

    const canSubmit =
        (!!value || (imageUpload?.images.length ?? 0) > 0) && !imageUpload?.isUploading

    return (
        <form
            onSubmit={(e) => {
                e.preventDefault()
                onSubmit()
            }}
            className="flex flex-col w-full bg-white-100 rounded-lg border border-white-200 divide-y divide-white-200"
        >
            <div className="px-3 py-3 cursor-text">
                <CommentEditor
                    ref={editorRef}
                    value={value}
                    onChange={onChange}
                    onSubmit={onSubmit}
                    placeholder={placeholder}
                    disabled={disabled}
                    mentionDirection={mentionDirection}
                />
            </div>
            {imageUpload && <CommentImageComposeStrip imageUpload={imageUpload} />}
            <div className="flex items-center justify-between pl-3 pr-2 py-1.5">
                <div className="flex items-center gap-1">
                    <EmojiPickerDropdown
                        onSelect={insertEmoji}
                        className="rounded-lg text-gray-300 hover:text-white"
                    >
                        <Icons.Icon Icon={Icons.AddReaction} />
                    </EmojiPickerDropdown>
                    <IconButton
                        onClick={() => editorRef.current?.insertMentionTrigger()}
                        className="flex items-center justify-center rounded-lg text-gray-300 hover:text-white"
                    >
                        <Icons.Icon Icon={Icons.AlternateEmail} />
                    </IconButton>
                    {imageUpload && (
                        <>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                multiple
                                hidden
                                onChange={(e) => {
                                    Array.from(e.target.files ?? []).forEach(imageUpload.upload)
                                    e.target.value = ""
                                }}
                            />
                            <IconButton
                                onClick={() => fileInputRef.current?.click()}
                                disabled={imageUpload.isFull}
                                className="flex items-center justify-center size- rounded-lg text-gray-300 hover:text-white disabled:text-gray-500 disabled:hover:text-gray-500"
                            >
                                <Icons.Icon Icon={Icons.AddPhotoAlternate} />
                            </IconButton>
                        </>
                    )}
                </div>
                <IconButton
                    type="submit"
                    className={classNames(
                        "flex items-center justify-center rounded-lg text-gray-300 px-3 py-2",
                        canSubmit &&
                            "bg-blue-500 text-gray-900 border-y border-b-black-300 border-t-transparent hover:bg-blue-200 hover:shadow-button-hover hover:border-t-white-200 active:bg-blue-500 active:shadow-button-active active:border-0"
                    )}
                    disabled={!canSubmit || disabled}
                >
                    <Icons.SmallIcon Icon={Icons.Send} />
                </IconButton>
            </div>
        </form>
    )
}
